import { URI } from "../../../../base/common/uri.js";
import { IOpenData } from "./project.js";

/**
 * Source-asset provenance: which LOADED FILE (and, for glTF, which element in
 * it) an edited object belongs to.
 *
 * The chain leans on GLTFLoader's `parser.associations` — a
 * `Map<Object3D | Material | Texture | …, { nodes?|materials?|meshes?|textures?: index }>`
 * the loader builds while parsing. So a material/texture/node that came out of
 * an open glb resolves DIRECTLY to its file + glTF index. This is the same map
 * the project's EditsModel uses to patch edits back into the source file — the
 * resolver just makes that lookup reusable (e.g. so the changeset can attribute
 * each edit to a file without a rewrite).
 *
 * Best-effort by design: non-glTF sources (FBX/OBJ) and editor-created objects
 * have no associations → `null` (they simply aren't attributable to a file yet).
 */

export type GltfElementKind = "nodes" | "materials" | "meshes" | "textures" | "cameras" | "skins" | "animations";

export interface SourceAssetRef {
    /** The source file the object was loaded from. */
    readonly uri: URI;
    /** Display file name (last path segment). */
    readonly fileName: string;
    /** The glTF element this object maps to, when resolved via associations. */
    readonly gltf?: { readonly kind: GltfElementKind; readonly index: number };
    /**
     * "association" = the object itself is a glTF element (precise);
     * "hierarchy" = matched via an ancestor node (the exact owner wasn't keyed).
     */
    readonly via: "association" | "hierarchy";
}

// order matters: the most specific/useful kind first when an association carries
// several (a mesh primitive is `{ meshes, primitives }`)
const GLTF_KINDS: readonly GltfElementKind[] = ["materials", "textures", "meshes", "nodes", "cameras", "skins", "animations"];
const MAX_PARENT_HOPS = 64;

/**
 * Resolve which open asset an edited object (material, texture, node, mesh…)
 * belongs to. Null when it can't be attributed to any open file.
 */
export function resolveSourceAsset(openFiles: readonly IOpenData[] | undefined, owner: object | undefined): SourceAssetRef | null {
    if (!openFiles?.length || !owner || typeof owner !== "object") return null;

    // 1) direct: the owner is itself a glTF element (material/texture/node/mesh)
    const direct = lookup(openFiles, owner, "association");
    if (direct) return direct;

    // 1a) the FILE ROOT itself: loaders do not reliably key the scene object in
    // `associations`, and the ancestor walk below goes UP and AWAY from a root —
    // so anything owned by the root (its own properties, components hanging off
    // it via the gameObject hop) was unattributable and its edits were silently
    // dropped at save (Marcel 2026-07-30: object-slot assign on a cloud asset's
    // root component never reached the document).
    const viaRoot = lookupRoot(openFiles, owner);
    if (viaRoot) return viaRoot;

    // 1b) an ANIMATION CLIP. GLTFLoader never puts clips in `associations` (verified
    // against three 0.185: loadAnimation builds the clip and returns it), so the
    // lookup above can't see them — which left every clip edit unattributable. We
    // don't need the loader to tell us though: for an open file we keep the load
    // RESULT, and `gltf.animations` holds the parsed clips, so identity is enough.
    // Cloned/retargeted clips still miss — see plans/animations-as-resources.md.
    const viaClip = lookupAnimation(openFiles, owner);
    if (viaClip) return viaClip;

    // 2) a COMPONENT: neither a glTF element nor part of the scene graph — it hangs off a
    // node via `gameObject`, not `parent`, so both the direct lookup and the ancestor walk
    // below miss it and the edit would be dropped as unattributable. Duck-typed: this
    // module stays engine-free.
    const host = (owner as { gameObject?: unknown }).gameObject;
    if (host && typeof host === "object") {
        const viaHost = resolveSourceAsset(openFiles, host as object);
        if (viaHost) return viaHost;
    }

    // 3) an Object3D whose exact node isn't keyed → walk up to a keyed ancestor
    // (or to a file ROOT — see 1a: roots are often not in associations either)
    let node = owner as { parent?: unknown };
    for (let hops = 0; node && typeof node === "object" && "parent" in node && node.parent && hops < MAX_PARENT_HOPS; hops++) {
        node = node.parent as { parent?: unknown };
        const viaAncestor = lookup(openFiles, node, "hierarchy") ?? lookupRoot(openFiles, node);
        if (viaAncestor) return viaAncestor;
    }
    return null;
}

/** The open file whose loaded scene root IS the object (no association needed). */
function lookupRoot(openFiles: readonly IOpenData[], owner: object): SourceAssetRef | null {
    for (const open of openFiles) {
        if (open.gltf?.scene === owner) {
            return { uri: open.uri, fileName: fileNameOf(open), via: "hierarchy" };
        }
    }
    return null;
}

/**
 * The scene-graph node an edit owner belongs to: itself for a node, the HOST node
 * for a component (`gameObject` hop — components hang off nodes via `gameObject`,
 * they are not in the graph and a parent walk from one goes nowhere).
 */
export function graphAnchorOf(owner: unknown): object | undefined {
    if (!owner || typeof owner !== "object") return undefined;
    if ((owner as { isObject3D?: boolean }).isObject3D === true) return owner as object;
    const host = (owner as { gameObject?: unknown }).gameObject;
    return host && typeof host === "object" && (host as { isObject3D?: boolean }).isObject3D === true ? host as object : undefined;
}

/**
 * THE containment resolver: which candidate's `root` contains `owner` — the root
 * ITSELF included, components included (gameObject hop), innermost root winning
 * when roots nest (the walk checks every candidate per hop, outward).
 *
 * Use this (or {@link resolveSourceAsset}) wherever "which file/reference owns
 * this object" is asked. NEVER hand-roll the parent walk: the reference root is
 * not an ordinary node — it is absent from loader `associations`, and a walk
 * that starts at `parent` leaves the file before ever testing the root. That
 * asymmetry produced the SAME silent-drop bug three times in separate copies
 * (address resolution, attribution, assign resolution) before this was made
 * one function.
 */
export function findContainingRoot<T extends { root: object }>(candidates: readonly T[], owner: object): T | undefined {
    if (candidates.length === 0) return undefined;
    let node: object | undefined = graphAnchorOf(owner) ?? owner;
    for (let hops = 0; node && typeof node === "object" && hops < MAX_PARENT_HOPS; hops++) {
        const current = node;
        const hit = candidates.find(c => c.root === current);
        if (hit) return hit;
        node = (node as { parent?: object | null }).parent ?? undefined;
    }
    return undefined;
}

function lookup(openFiles: readonly IOpenData[], owner: object, via: SourceAssetRef["via"]): SourceAssetRef | null {
    for (const open of openFiles) {
        const associations = open.gltf?.parser?.associations as Map<object, Record<string, number>> | undefined;
        const assoc = associations?.get(owner);
        if (!assoc) continue;
        const kind = GLTF_KINDS.find(k => typeof assoc[k] === "number");
        return {
            uri: open.uri,
            fileName: fileNameOf(open),
            gltf: kind ? { kind, index: assoc[kind]! } : undefined,
            via,
        };
    }
    return null;
}

/** The open file an AnimationClip came from, matched by identity against the
 *  file's parsed `animations`. Duck-typed (`tracks`) so this module stays
 *  engine-free, like the component lookup above. */
function lookupAnimation(openFiles: readonly IOpenData[], owner: object): SourceAssetRef | null {
    if (!Array.isArray((owner as { tracks?: unknown }).tracks)) return null;
    for (const open of openFiles) {
        const animations = open.gltf?.animations;
        if (!Array.isArray(animations)) continue;
        const index = animations.indexOf(owner);
        if (index < 0) continue;
        return {
            uri: open.uri,
            fileName: fileNameOf(open),
            gltf: { kind: "animations", index },
            via: "association",
        };
    }
    return null;
}

function fileNameOf(open: IOpenData): string {
    const path = open.uri.path || open.uri.toString();
    const segment = decodeURIComponent(path.split("/").pop() || "");
    return segment || open.uri.toString();
}
